import type { QuotaState, Pace } from '../core/types.js';

function periodMs(p: 'daily' | 'weekly' | 'monthly'): number {
  if (p === 'daily') return 24 * 3600 * 1000;
  if (p === 'weekly') return 7 * 24 * 3600 * 1000;
  return 30 * 24 * 3600 * 1000;
}

export function maybeRollPeriod(state: QuotaState, now: Date = new Date()): QuotaState {
  if (now.getTime() < new Date(state.periodEndsAt).getTime()) return state;
  // Roll forward as many full periods as needed so a long absence does not
  // leave the user with a stale "X left this period" message.
  const step = periodMs(state.period);
  let startedAt = new Date(state.periodEndsAt).getTime();
  let endsAt = startedAt + step;
  while (now.getTime() >= endsAt) {
    startedAt = endsAt;
    endsAt = startedAt + step;
  }
  return {
    ...state,
    periodStartedAt: new Date(startedAt).toISOString(),
    periodEndsAt: new Date(endsAt).toISOString(),
    usedThisPeriod: 0,
  };
}

export function applyUsage(state: QuotaState, _pace: Pace, now: Date = new Date()): { state: QuotaState; explanation: string; over: boolean } {
  return applyUsageNormalized(state, _pace, now, 1);
}

/**
 * Apply usage with an explicit domain-units increment so merged session taps
 * (vape puffs inside the merge window) count as one episode rather than N.
 */
export function applyUsageNormalized(
  state: QuotaState,
  _pace: Pace,
  now: Date,
  domainUnits: number,
): { state: QuotaState; explanation: string; over: boolean } {
  let s = maybeRollPeriod(state, now);
  const inc = domainUnits > 0 ? domainUnits : 0;
  s = { ...s, usedThisPeriod: s.usedThisPeriod + inc };
  const over = s.usedThisPeriod > s.currentQuota;
  const explanation = over
    ? `You went over this period’s allowance. Wean will not punish — try a rescue before the next one.`
    : `Logged. ${Math.max(0, s.currentQuota - s.usedThisPeriod)} left this period.`;
  return { state: { ...s, lastExplanation: explanation }, explanation, over };
}

export function closePeriodIfDue(state: QuotaState, _pace: Pace, now: Date = new Date()): { state: QuotaState; closed: boolean; outcome?: 'stretch_success' | 'success' | 'near_miss' | 'over_quota' } {
  if (now.getTime() < new Date(state.periodEndsAt).getTime()) return { state, closed: false };
  const used = state.usedThisPeriod;
  const q = state.currentQuota;
  let outcome: 'stretch_success' | 'success' | 'near_miss' | 'over_quota';
  if (used <= q * 0.80) outcome = 'stretch_success';
  else if (used <= q) outcome = 'success';
  else if (used <= q + 1) outcome = 'near_miss';
  else outcome = 'over_quota';
  let nextQuota = q;
  // Floor at 1: dropping to 0 would make the next period start instantly over
  // cap on first use, with no path to graduate from inside the engine.
  if (outcome === 'stretch_success') nextQuota = Math.max(1, q - state.reductionStep * 2);
  else if (outcome === 'success') nextQuota = Math.max(1, q - state.reductionStep);
  // near_miss / over_quota: hold
  const startedAt = new Date(state.periodEndsAt);
  const endsAt = new Date(startedAt.getTime() + periodMs(state.period));
  const explanation = outcome === 'stretch_success' ? 'Well under cap. Cap reduced.' :
    outcome === 'success' ? 'Under cap. Cap reduced.' :
    outcome === 'near_miss' ? 'Just over — cap held.' : 'Over cap — cap held; pace preserved.';
  const successPeriods = (outcome === 'success' || outcome === 'stretch_success') ? state.successPeriods + 1 : state.successPeriods;
  return {
    state: {
      ...state, currentQuota: nextQuota,
      periodStartedAt: startedAt.toISOString(),
      periodEndsAt: endsAt.toISOString(),
      usedThisPeriod: 0, successPeriods, lastExplanation: explanation,
    },
    closed: true, outcome,
  };
}
